import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Dialog, DialogContent, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import {
  MATERIAL_GROUPS, MATERIAL_UNIT_OPTIONS, materialGroupLabel, materialGroupValueFromLabel, type MaterialRateItem,
} from '../pages/OfficeEstimationPage';
import { hasVariety, sameMaterialName, similarMaterials, type NewMaterialInput } from '../office/newMaterial';
import { BORDER, DARK, GOLD_D, MUTED, OutlineButton, PrimaryButton, ScreenBar, WARN } from './phoneUi';

const norm = (value?: string) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Adds a material (or a variety of one) to the shared library without leaving the order panel.
 * The library is shared by every project, so it warns about materials that look the same first.
 */
export default function NewMaterialDialog({ open, initialName, items, onClose, onCreate, onAddVariety, onUse }: {
  open: boolean;
  initialName: string;
  items: MaterialRateItem[];
  onClose: () => void;
  onCreate: (input: NewMaterialInput) => Promise<void>;
  onAddVariety: (item: MaterialRateItem, assemblyType: string, variety: string) => Promise<void>;
  /** Order an existing material (and the assembly type / variety typed, when it already has them). */
  onUse: (item: MaterialRateItem, assemblyType: string, variety: string) => void;
}) {
  const fullScreen = !useMediaQuery('(min-width:900px)');
  const [group, setGroup] = useState('');
  const [name, setName] = useState(initialName);
  const [unit, setUnit] = useState('');
  const [assemblyType, setAssemblyType] = useState('');
  const [showAssembly, setShowAssembly] = useState(false);
  const [variety, setVariety] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setName(initialName); setGroup(''); setUnit(''); setAssemblyType(''); setShowAssembly(false); setVariety(''); setError(''); } }, [open, initialName]);

  const groupChoices = useMemo(() => {
    const builtIns = MATERIAL_GROUPS.filter((key) => key !== 'labour').map((key) => materialGroupLabel(key));
    const custom = new Set<string>();
    items.forEach((item) => { const label = materialGroupLabel(String(item.group)).trim(); if (label && item.group !== 'labour' && !builtIns.some((option) => norm(option) === norm(label))) custom.add(label); });
    return [...builtIns, ...Array.from(custom).sort((a, b) => a.localeCompare(b))];
  }, [items]);

  const groupValue = group.trim() ? String(materialGroupValueFromLabel(group)) : '';
  const unitInGroup = items.find((item) => norm(String(item.group)) === norm(groupValue))?.unit || '';
  const chosenUnit = unit || unitInGroup || 'Nos';
  // The same material name is the same material, so a group that was not filled in is not needed.
  const exact = items.find((item) => sameMaterialName(item, name) && (!groupValue || norm(String(item.group)) === norm(groupValue)));
  const existingAssemblies = exact ? Array.from(new Set([...(exact.assemblyVarietyMap || []).map((entry) => entry.assemblyType), ...(exact.assemblyTypes || [])].map((value) => String(value || '').trim()).filter(Boolean))) : [];
  const similar = similarMaterials(items, name).filter((item) => item !== exact);
  const kindTyped = !!(assemblyType.trim() || variety.trim());
  const alreadyThere = !!exact && (!kindTyped || hasVariety(exact, assemblyType, variety));

  const run = async (work: () => Promise<void> | void) => {
    setSaving(true); setError('');
    try { await work(); } catch (err: any) { setError(err?.message || 'Could not save. Try again.'); } finally { setSaving(false); }
  };
  const submit = () => {
    if (!name.trim()) { setError('Enter the material name.'); return; }
    if (!groupValue && !exact) { setError('Choose or type a group.'); return; }
    if (exact && alreadyThere) { onUse(exact, assemblyType, variety); return; }
    if (exact) return run(() => onAddVariety(exact, assemblyType, variety));
    return run(() => onCreate({ group: groupValue, materialName: name.trim(), unit: chosenUnit, assemblyType: assemblyType.trim(), variety: variety.trim() }));
  };

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
    <ScreenBar title="Add new material" sub="Saved to the shared library, so every project can use it" onBack={saving ? undefined : onClose} />
    <DialogContent>
      <Stack gap={1.75} sx={{ pt: 0.5 }}>
        <Autocomplete freeSolo options={groupChoices} inputValue={group} onInputChange={(_, value) => setGroup(value)}
          renderInput={(params) => <TextField {...params} label="Group" placeholder="Choose, or type a new group" inputProps={{ ...params.inputProps, 'aria-label': 'Group' }} />} />
        <TextField label="Material name" value={name} onChange={(event) => { setName(event.target.value); setError(''); }} inputProps={{ 'aria-label': 'Material name' }} fullWidth />
        <Autocomplete disableClearable autoHighlight openOnFocus options={MATERIAL_UNIT_OPTIONS.includes(chosenUnit) ? MATERIAL_UNIT_OPTIONS : [chosenUnit, ...MATERIAL_UNIT_OPTIONS]} value={chosenUnit} onChange={(_, value) => setUnit(value)}
          renderInput={(params) => <TextField {...params} label="Unit" placeholder="Search unit" inputProps={{ ...params.inputProps, 'aria-label': 'Unit' }} />} />
        <TextField label="Variety (optional)" placeholder="Only if it comes in kinds, like Sieved Washed Bodeli" value={variety} onChange={(event) => setVariety(event.target.value)} inputProps={{ 'aria-label': 'Variety' }} fullWidth />
        {exact && existingAssemblies.length > 0
          ? <Autocomplete freeSolo options={existingAssemblies} inputValue={assemblyType} onInputChange={(_, value) => setAssemblyType(value)}
            renderInput={(params) => <TextField {...params} label="Assembly type (optional)" placeholder={`Which kind of ${exact.materialName}`} inputProps={{ ...params.inputProps, 'aria-label': 'Assembly type' }} />} />
          : showAssembly
            ? <TextField label="Assembly type (optional)" placeholder="Only if different from the material name" value={assemblyType} onChange={(event) => setAssemblyType(event.target.value)} inputProps={{ 'aria-label': 'Assembly type' }} fullWidth />
            : <Button onClick={() => setShowAssembly(true)} sx={{ alignSelf: 'flex-start', color: GOLD_D, fontWeight: 600, textTransform: 'none' }}>+ Add assembly type</Button>}

        {exact && <Box sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: '15px', bgcolor: '#fffaf0' }}>
          <Typography sx={{ fontSize: 13.5, color: DARK }}>
            {alreadyThere ? `${exact.materialName} is already in the library${kindTyped ? ' with this variety' : ''}. Use it instead.` : `${exact.materialName} is already in the library. This will add the new variety to it.`}
          </Typography>
        </Box>}
        {!exact && similar.length > 0 && <Stack gap={0.75}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: WARN }}>Already in the library — is it one of these?</Typography>
          {similar.slice(0, 5).map((item) => <Stack key={item.id} direction="row" alignItems="center" gap={1} sx={{ p: 1, border: `1px solid ${BORDER}`, borderRadius: '15px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontSize: 14, color: DARK }}>{item.materialName}</Typography><Typography sx={{ fontSize: 12, color: MUTED }}>{materialGroupLabel(String(item.group))}</Typography></Box>
            <Button size="small" onClick={() => onUse(item, '', '')} sx={{ color: GOLD_D, fontWeight: 600 }}>Use this</Button>
          </Stack>)}
        </Stack>}
        {error && <Typography sx={{ color: 'error.main', fontSize: 13 }}>{error}</Typography>}
        <Stack direction="row" gap={1}>
          <OutlineButton onClick={onClose} disabled={saving} fullWidth>Cancel</OutlineButton>
          <PrimaryButton onClick={submit} disabled={saving} fullWidth>{saving ? 'Saving…' : exact ? (alreadyThere ? `Use ${exact.materialName}` : `Add variety to ${exact.materialName}`) : 'Add material'}</PrimaryButton>
        </Stack>
      </Stack>
    </DialogContent>
  </Dialog>;
}
